import {useCallback, useRef, useState} from 'react'

import {TranscriptLine, VoiceCallError, VoiceCallStatus} from './useVoiceCallState'

export interface VoiceCallSharedRefs {
  statusRef: React.MutableRefObject<VoiceCallStatus>
  transcriptionEnabledRef: React.MutableRefObject<boolean | null>
  permissionDeniedRef: React.MutableRefObject<boolean>
  awaitingResponseRef: React.MutableRefObject<boolean>
  isAiSpeakingRef: React.MutableRefObject<boolean>
  mutedRef: React.MutableRefObject<boolean>
  inFlightRef: React.MutableRefObject<boolean>
  responseRequestedRef: React.MutableRefObject<boolean>
  abortRef: React.MutableRefObject<AbortController | null>
  listeningBlockedRef: React.MutableRefObject<boolean>
  blockingReasonRef: React.MutableRefObject<string>
  interruptListeningActiveRef: React.MutableRefObject<boolean>
  ignoreRecognizerDuringAiRef: React.MutableRefObject<boolean>
  isAudioPlayingRef: React.MutableRefObject<boolean>
  walkthroughActiveRef: React.MutableRefObject<boolean>
  interimUserTranscriptRef: React.MutableRefObject<string>
  lastAudioActivityRef: React.MutableRefObject<number>
  speechStartTimeRef: React.MutableRefObject<number>
  realSpeechDetectedRef: React.MutableRefObject<boolean>
  hasVoiceActivityRef: React.MutableRefObject<boolean>
  sustainedSpeechRef: React.MutableRefObject<boolean>
  consecutiveRealSpeechRef: React.MutableRefObject<number>
  voiceActivityBufferRef: React.MutableRefObject<number[]>
  maxListeningTimeRef: React.MutableRefObject<number>
  noiseFloorRef: React.MutableRefObject<number>
  noActivityTimeRef: React.MutableRefObject<number>
  lastCommittedUserTranscriptRef: React.MutableRefObject<string>
  timeoutIdsRef: React.MutableRefObject<Set<number>>
  callSummaryInsertedRef: React.MutableRefObject<boolean>
}

export const useVoiceCallStateRegistry = () => {
  const [status, setStatus] = useState<VoiceCallStatus>('idle')
  const [error, setError] = useState<VoiceCallError | null>(null)
  const [transcript, setTranscript] = useState<TranscriptLine[]>([])
  const [interimUserTranscript, setInterimUserTranscript] = useState('')
  const [micLevel, setMicLevel] = useState(0)
  const [awaitingResponse, setAwaitingResponse] = useState(false)
  const [isAiSpeaking, setIsAiSpeaking] = useState(false)

  const statusRef = useRef<VoiceCallStatus>(status)
  const transcriptionEnabledRef = useRef<boolean | null>(null)
  const awaitingResponseRef = useRef<boolean>(awaitingResponse)
  const isAiSpeakingRef = useRef<boolean>(isAiSpeaking)
  const mutedRef = useRef<boolean>(false)
  const permissionDeniedRef = useRef<boolean>(false)

  const inFlightRef = useRef<boolean>(false)
  const responseRequestedRef = useRef<boolean>(false)
  const abortRef = useRef<AbortController | null>(null)

  const listeningBlockedRef = useRef<boolean>(false)
  const blockingReasonRef = useRef<string>('')
  const interruptListeningActiveRef = useRef<boolean>(false)
  const ignoreRecognizerDuringAiRef = useRef<boolean>(false)
  const isAudioPlayingRef = useRef<boolean>(false)
  const walkthroughActiveRef = useRef<boolean>(false)

  const interimUserTranscriptRef = useRef<string>('')
  const lastAudioActivityRef = useRef<number>(0)
  const speechStartTimeRef = useRef<number>(0)
  const realSpeechDetectedRef = useRef<boolean>(false)
  const hasVoiceActivityRef = useRef<boolean>(false)
  const sustainedSpeechRef = useRef<boolean>(false)
  const consecutiveRealSpeechRef = useRef<number>(0)
  const voiceActivityBufferRef = useRef<number[]>([])
  const maxListeningTimeRef = useRef<number>(Date.now())
  const noiseFloorRef = useRef<number>(0.01)
  const noActivityTimeRef = useRef<number>(Date.now())

  const lastCommittedUserTranscriptRef = useRef<string>('')
  const timeoutIdsRef = useRef<Set<number>>(new Set())
  const callSummaryInsertedRef = useRef<boolean>(false)

  const userTranscriptExists = useCallback(
    (text: string) => {
      return transcript.some(
        (l) => l.speaker === 'user' && l.text.toLowerCase() === text.toLowerCase()
      )
    },
    [transcript]
  )

  const refs: VoiceCallSharedRefs = {
    statusRef,
    transcriptionEnabledRef,
    awaitingResponseRef,
    isAiSpeakingRef,
    mutedRef,
    permissionDeniedRef,
    inFlightRef,
    responseRequestedRef,
    abortRef,
    listeningBlockedRef,
    blockingReasonRef,
    interruptListeningActiveRef,
    ignoreRecognizerDuringAiRef,
    isAudioPlayingRef,
    walkthroughActiveRef,
    interimUserTranscriptRef,
    lastAudioActivityRef,
    speechStartTimeRef,
    realSpeechDetectedRef,
    hasVoiceActivityRef,
    sustainedSpeechRef,
    consecutiveRealSpeechRef,
    voiceActivityBufferRef,
    maxListeningTimeRef,
    noiseFloorRef,
    noActivityTimeRef,
    lastCommittedUserTranscriptRef,
    timeoutIdsRef,
    callSummaryInsertedRef,
  }

  return {
    state: {
      status,
      setStatus,
      error,
      setError,
      transcript,
      setTranscript,
      interimUserTranscript,
      setInterimUserTranscript,
      micLevel,
      setMicLevel,
      awaitingResponse,
      setAwaitingResponse,
      isAiSpeaking,
      setIsAiSpeaking,
    },
    refs,
    userTranscriptExists,
  }
}
